import { useDocumentVerificationMode } from '../../../module-system/use-document-verification-mode.js';
import { VerificationHistory } from '../../../components/verification/VerificationHistory.jsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi } from './../../../lib/admin-api/index.js';
import {
  isInvalidSecurityStepUpCodeError,
  isSecurityStepUpRequiredError,
  isSensitiveActionRestrictedError,
} from './../../../lib/admin-api/http-client.js';
import SecurityStepUpModal from '../../../modules/profiles/ui/security-step-up-modal.jsx';

const SENSITIVE_ACTION_RESTRICTED_MESSAGE = 'Sensitive action restricted';

function createSecurityRequestId() {
  return `admin-step-up-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function normalizeDocumentType(type) {
  return String(type || '').trim().toLowerCase().replace(/_/g, '-');
}

function formatDocumentTypeLabel(type) {
  const normalized = normalizeDocumentType(type).replace(/-/g, ' ').trim();
  if (!normalized) {
    return 'Document';
  }

  return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
}

function getDocumentStatusMeta(document) {
  if (!document) {
    return { label: 'Missing', className: 'app-badge-disabled' };
  }

  const rawStatus = String(pickFirst(document.status) || 'Uploaded').trim();
  const normalizedStatus = rawStatus.toLowerCase();

  if (normalizedStatus.includes('approved') || normalizedStatus.includes('verified')) {
    return { label: rawStatus, className: 'bg-success' };
  }
  if (normalizedStatus.includes('reject') || normalizedStatus.includes('declin')) {
    return { label: rawStatus, className: 'bg-danger' };
  }
  if (normalizedStatus.includes('pending') || normalizedStatus.includes('review')) {
    return { label: rawStatus, className: 'bg-warning text-dark' };
  }

  return { label: rawStatus, className: 'bg-primary' };
}

function fieldValue(...values) {
  return pickFirst(...values) || '';
}

function normalizeList(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === 'object') {
    return Object.values(value);
  }

  return [];
}

function normalizeKycSteps(payload) {
  const candidates = [
    payload?.steps,
    payload?.items,
    payload?.data?.steps,
    payload?.data?.items,
    payload,
  ];

  for (const candidate of candidates) {
    const list = normalizeList(candidate);
    if (list.length > 0) {
      return list;
    }
  }

  return [];
}

function normalizeTextValue(value) {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    const raw = value.trim();
    if (!raw) {
      return '';
    }

    const looksLikeJson = (raw.startsWith('{') && raw.endsWith('}')) || (raw.startsWith('[') && raw.endsWith(']'));
    if (!looksLikeJson) {
      return raw;
    }

    try {
      return normalizeTextValue(JSON.parse(raw));
    } catch {
      return raw;
    }
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeTextValue(item)).filter(Boolean).join(', ');
  }

  if (typeof value === 'object') {
    return pickFirst(value.value, value.label, value.name, value.text) || JSON.stringify(value);
  }

  return String(value);
}

function buildFirstStepFieldNameMap(firstSettingsStep) {
  const map = new Map();
  normalizeList(firstSettingsStep?.fields).forEach((field, index) => {
    const fieldId = String(pickFirst(field?.uid, field?.id, field?.fieldId, `field-${index}`));
    const fieldName = String(pickFirst(field?.name, field?.label, field?.title) || '').trim();
    if (fieldId && fieldName) {
      map.set(fieldId, fieldName);
    }
  });
  return map;
}

function normalizeKey(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function extractFirstKycStepFields(kycData, kycSettingsSteps) {
  const firstStep = normalizeKycSteps(kycData)[0];
  if (!firstStep) {
    return [];
  }

  const firstSettingsStep = normalizeList(kycSettingsSteps)[0] || null;
  const settingsNameByFieldId = buildFirstStepFieldNameMap(firstSettingsStep);
  const items = normalizeList(firstStep?.items);

  return items.map((item, index) => {
    const fieldId = String(pickFirst(item?.fieldId, item?.uid, item?.id, `field-${index}`));
    const sourceName = pickFirst(
      item?.fieldName,
      item?.field_name,
      item?.name,
      settingsNameByFieldId.get(fieldId),
      fieldId,
    );
    const normalizedName = String(sourceName || '').trim();
    const value = normalizeTextValue(item?.data);

    return {
      id: fieldId,
      name: normalizedName || `Field ${index + 1}`,
      key: normalizeKey(normalizedName || fieldId),
      value: value || '-',
    };
  });
}

function ProfileVerificationTab({
  profile,
  documents,
  kycData,
  kycSettingsSteps,
  token,
  canViewExternalVerifications = false,
  onExternalVerificationChanged,
  onVerifyProfile,
  onRequestInformation,
  onInvalidateProfile,
  onValidateProfile,
  isVerifying,
  isRequestingInformation,
  isInvalidatingProfile,
  isValidatingProfile,
  verifyError,
  verifySuccess,
  showInlineAlerts = true,
  onOpenDocument,
}) {
  const { t } = useTranslation();
  const documentVerificationMode = useDocumentVerificationMode(token);
  const [previewById, setPreviewById] = useState({});
  const [refreshingPreviewById, setRefreshingPreviewById] = useState({});
  const [showOtherDocuments, setShowOtherDocuments] = useState(false);
  const [mobileSection, setMobileSection] = useState('profile');
  const [profileSection, setProfileSection] = useState('profileInformation');
  const [invalidationAction, setInvalidationAction] = useState(null);
  const [invalidationReason, setInvalidationReason] = useState('');
  const [invalidationReasonError, setInvalidationReasonError] = useState('');
  const [documentSecurityStepUp, setDocumentSecurityStepUp] = useState({
    isOpen: false,
    isSubmitting: false,
    error: '',
  });
  const previewObjectUrlByIdRef = React.useRef({});
  const documentSecurityStepUpRef = React.useRef(null);
  const isProfileInvalid = profile?.invalid === true;
  const isProfileInvalidationSubmitting = isInvalidatingProfile || isValidatingProfile;
  const isProfileActionBusy = isVerifying || isRequestingInformation || isProfileInvalidationSubmitting;

  const requiredDocumentAliases = useMemo(
    () => [
      { key: 'id-front', label: t('profiles.docIdFront') },
      { key: 'id-back', label: t('profiles.docIdBack') },
      { key: 'poa', label: t('profiles.docPoa') },
    ],
    [t],
  );

  const requiredDocuments = useMemo(() => (
    requiredDocumentAliases.map((item) => {
      const found = documents.find((doc) => normalizeDocumentType(doc?.type) === item.key);
      return {
        ...item,
        document: found || null,
      };
    })
  ), [documents, requiredDocumentAliases]);

  const allDocumentCards = useMemo(() => {
    const requiredKeys = new Set(requiredDocumentAliases.map((item) => item.key));
    const otherByType = new Map();

    documents.forEach((doc, index) => {
      const normalizedType = normalizeDocumentType(doc?.type);
      if (!normalizedType || requiredKeys.has(normalizedType) || otherByType.has(normalizedType)) {
        return;
      }

      otherByType.set(normalizedType, {
        key: `other-${normalizedType}-${index}`,
        label: formatDocumentTypeLabel(doc?.type),
        document: doc,
      });
    });

    return [...requiredDocuments, ...Array.from(otherByType.values())];
  }, [documents, requiredDocumentAliases, requiredDocuments]);

  const visibleDocumentCards = useMemo(() => (
    documentVerificationMode === 'external' ? allDocumentCards.filter((item) => item.document) : (showOtherDocuments ? allDocumentCards : requiredDocuments)
  ), [allDocumentCards, requiredDocuments, showOtherDocuments, documentVerificationMode]);

  const closeDocumentSecurityStepUp = React.useCallback(() => {
    documentSecurityStepUpRef.current = null;
    setDocumentSecurityStepUp({
      isOpen: false,
      isSubmitting: false,
      error: '',
    });
  }, []);

  const loadDocumentPreview = React.useCallback(async (docId, options = {}) => {
    const normalizedDocId = String(docId || '').trim();
    if (!normalizedDocId) {
      return false;
    }

    const {
      create = false,
      requestId = createSecurityRequestId(),
      isCancelled = () => false,
      markRefreshing = false,
    } = options;

    if (markRefreshing && !isCancelled()) {
      setRefreshingPreviewById((prev) => ({
        ...prev,
        [normalizedDocId]: true,
      }));
    }
    if (!isCancelled()) {
      setPreviewById((prev) => ({
        ...prev,
        [normalizedDocId]: {
          ...(prev[normalizedDocId] || {}),
          isLoading: true,
          error: '',
        },
      }));
    }

    try {
      const response = await adminApi.requestBlobWithAuth({
        token,
        method: 'GET',
        path: `/admin/document/client/download/${encodeURIComponent(normalizedDocId)}/preview${create ? '?create=1' : ''}`,
        headers: {
          'X-Request-Id': requestId,
        },
      });

      if (isCancelled()) {
        return false;
      }

      const blob = response.blob;
      const objectUrl = URL.createObjectURL(blob);

      const previousUrl = previewObjectUrlByIdRef.current[normalizedDocId];
      if (previousUrl) {
        URL.revokeObjectURL(previousUrl);
      }
      previewObjectUrlByIdRef.current[normalizedDocId] = objectUrl;

      setPreviewById((prev) => ({
        ...prev,
        [normalizedDocId]: {
          ...(prev[normalizedDocId] || {}),
          isLoading: false,
          error: '',
          url: objectUrl,
          mime: blob.type || '',
        },
      }));
      return true;
    } catch (error) {
      if (isSecurityStepUpRequiredError(error)) {
        if (!isCancelled()) {
          documentSecurityStepUpRef.current = {
            requestId,
            retry: () => loadDocumentPreview(normalizedDocId, {
              create,
              requestId,
              markRefreshing,
            }),
          };
          setPreviewById((prev) => ({
            ...prev,
            [normalizedDocId]: {
              ...(prev[normalizedDocId] || {}),
              isLoading: false,
              error: '',
            },
          }));
          setDocumentSecurityStepUp({
            isOpen: true,
            isSubmitting: false,
            error: '',
          });
        }
        return false;
      }

      const fallbackMessage = create ? 'Failed to refresh preview' : 'Failed to load preview';
      const errorMessage = isSensitiveActionRestrictedError(error)
        ? error.message || SENSITIVE_ACTION_RESTRICTED_MESSAGE
        : error.message || fallbackMessage;

      if (!isCancelled()) {
        setPreviewById((prev) => ({
          ...prev,
          [normalizedDocId]: {
            ...(prev[normalizedDocId] || {}),
            isLoading: false,
            error: errorMessage,
          },
        }));
      }
      return false;
    } finally {
      if (markRefreshing && !isCancelled()) {
        setRefreshingPreviewById((prev) => ({
          ...prev,
          [normalizedDocId]: false,
        }));
      }
    }
  }, [token]);

  const submitDocumentSecurityStepUp = React.useCallback(async (code) => {
    const action = documentSecurityStepUpRef.current;
    const normalizedCode = String(code || '').trim();
    if (!action) {
      return false;
    }
    if (!normalizedCode) {
      setDocumentSecurityStepUp((prev) => ({
        ...prev,
        error: t('securityStepUp.codeRequired'),
      }));
      return false;
    }

    setDocumentSecurityStepUp((prev) => ({
      ...prev,
      isSubmitting: true,
      error: '',
    }));

    try {
      await adminApi.verifySecurityStepUp(token, { code: normalizedCode }, {
        requestId: action.requestId,
      });
      await action.retry();
      closeDocumentSecurityStepUp();
      return true;
    } catch (error) {
      setDocumentSecurityStepUp((prev) => ({
        ...prev,
        isSubmitting: false,
        error: isInvalidSecurityStepUpCodeError(error)
          ? t('securityStepUp.invalidCode')
          : error.message || t('securityStepUp.verifyFailed'),
      }));
      return false;
    }
  }, [closeDocumentSecurityStepUp, t, token]);

  const openInvalidationModal = (action) => {
    setInvalidationAction(action);
    setInvalidationReason('');
    setInvalidationReasonError('');
  };

  const closeInvalidationModal = () => {
    if (isProfileInvalidationSubmitting) {
      return;
    }

    setInvalidationAction(null);
    setInvalidationReason('');
    setInvalidationReasonError('');
  };

  const submitInvalidationAction = async () => {
    const reason = String(invalidationReason || '').trim();
    if (!reason) {
      setInvalidationReasonError(t('profiles.invalidationReasonRequired'));
      return;
    }

    setInvalidationReasonError('');
    if (invalidationAction === 'invalidate') {
      await onInvalidateProfile?.(reason);
    } else if (invalidationAction === 'validate') {
      await onValidateProfile?.(reason);
    }

    setInvalidationAction(null);
    setInvalidationReason('');
  };

  useEffect(() => {
    let isCancelled = false;

    const docsToLoad = visibleDocumentCards
      .map((item) => item.document)
      .filter((doc) => doc && pickFirst(doc.uid, doc.id) && doc.hasPreview === true);

    if (!docsToLoad.length) {
      setPreviewById({});
      return undefined;
    }

    const loadPreviews = async () => {
      await Promise.all(docsToLoad.map((doc) => (
        loadDocumentPreview(String(pickFirst(doc.uid, doc.id)), {
          isCancelled: () => isCancelled,
        })
      )));
    };

    loadPreviews();

    return () => {
      isCancelled = true;
    };
  }, [loadDocumentPreview, visibleDocumentCards]);

  useEffect(() => () => {
    Object.values(previewObjectUrlByIdRef.current).forEach((url) => {
      URL.revokeObjectURL(url);
    });
    previewObjectUrlByIdRef.current = {};
  }, []);

  const refreshDocumentPreview = React.useCallback(async (docId) => {
    if (!docId) {
      return;
    }

    await loadDocumentPreview(docId, {
      create: true,
      markRefreshing: true,
    });
  }, [loadDocumentPreview]);

  const firstKycStepFields = useMemo(
    () => extractFirstKycStepFields(kycData, kycSettingsSteps),
    [kycData, kycSettingsSteps],
  );
  const profileInformationContent = (
    <form>
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label">{t('profiles.firstName')}</label>
          <input type="text" className="form-control" value={fieldValue(profile?.firstName, profile?.first_name)} disabled />
        </div>
        <div className="col-md-6">
          <label className="form-label">{t('profiles.lastName')}</label>
          <input type="text" className="form-control" value={fieldValue(profile?.lastName, profile?.last_name)} disabled />
        </div>
        <div className="col-md-6">
          <label className="form-label">{t('common.email')}</label>
          <input type="text" className="form-control" value={fieldValue(profile?.emailMasked, profile?.email_masked)} disabled />
        </div>
        <div className="col-md-6">
          <label className="form-label">{t('profiles.phone')}</label>
          <input type="text" className="form-control" value={fieldValue(profile?.phoneMasked, profile?.phone_masked)} disabled />
        </div>
        <div className="col-md-6">
          <label className="form-label">{t('profiles.country')}</label>
          <input
            type="text"
            className="form-control"
            value={fieldValue(profile?.country, profile?.countryName, profile?.country_name)}
            disabled
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">{t('profiles.language')}</label>
          <input type="text" className="form-control" value={fieldValue(profile?.language)} disabled />
        </div>
      </div>
    </form>
  );

  const personalDataContent = (
    <form>
      <div className="row g-3">
        {firstKycStepFields.length === 0 && (
          <div className="col-12 text-muted">{t('profiles.noKycStepData')}</div>
        )}

        {firstKycStepFields.map((field) => (
          <div key={`${field.id}-${field.key}`} className="col-12">
            <label className="form-label">{field.name}</label>
            {String(field.value).includes('\n') || String(field.value).length > 120 ? (
              <textarea className="form-control" value={field.value} rows={3} disabled />
            ) : (
              <input type="text" className="form-control" value={field.value} disabled />
            )}
          </div>
        ))}
      </div>
    </form>
  );

  const renderProfileSectionTabs = (idPrefix) => {
    const tabs = [
      { key: 'profileInformation', label: t('profiles.profileInformation') },
      { key: 'personalData', label: t('profiles.personalData') },
    ];
    const panelId = `${idPrefix}-${profileSection}-panel`;

    return (
      <>
        <ul className="nav nav-tabs flex-nowrap mb-3" role="tablist">
          {tabs.map((tab) => {
            const isActive = profileSection === tab.key;
            return (
              <li className="nav-item" role="presentation" key={`${idPrefix}-${tab.key}`}>
                <button
                  id={`${idPrefix}-${tab.key}-tab`}
                  type="button"
                  role="tab"
                  className={`nav-link ${isActive ? 'active' : ''}`}
                  aria-selected={isActive}
                  aria-controls={panelId}
                  onClick={() => setProfileSection(tab.key)}
                >
                  {tab.label}
                </button>
              </li>
            );
          })}
        </ul>
        <div
          id={panelId}
          className="tab-pane fade active show"
          role="tabpanel"
          aria-labelledby={`${idPrefix}-${profileSection}-tab`}
        >
          {profileSection === 'personalData' ? personalDataContent : profileInformationContent}
        </div>
      </>
    );
  };

  const documentsContent = (switchId) => (
    <>
      <VerificationHistory token={token} profileId={profile?.uid || profile?.id} canView={canViewExternalVerifications} onChanged={onExternalVerificationChanged} />
      <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
        <h5 className="mb-0">{t('nav.documents')}</h5>
        <div className="form-check form-switch m-0">
          <input
            id={switchId}
            className="form-check-input"
            type="checkbox"
            role="switch"
            checked={showOtherDocuments}
            onChange={(event) => setShowOtherDocuments(event.target.checked)}
          />
          <label className="form-check-label small ms-2" htmlFor={switchId}>
            {t('profiles.showOtherDocuments')}
          </label>
        </div>
      </div>
      <div className="row g-3">
        {visibleDocumentCards.map((item) => {
          const doc = item.document;
          const docId = String(pickFirst(doc?.uid, doc?.id) || '');
          const preview = previewById[docId] || {};
          const hasImage = Boolean(doc?.hasPreview === true && preview.url && String(preview.mime || '').startsWith('image/'));
          const canOpenDetails = Boolean(doc);
          const canRefreshPreview = Boolean(doc?.hasPreview === true && docId);
          const isRefreshingPreview = Boolean(refreshingPreviewById[docId]);
          const statusMeta = getDocumentStatusMeta(doc);
          const documentType = pickFirst(doc?.type, item.label) || '-';

          return (
            <div key={item.key} className="col-6 col-md-4">
              <div className="d-flex flex-column gap-2">
                <div
                  className={`app-document-preview-surface app-document-preview-card border rounded d-flex align-items-center justify-content-center overflow-hidden ${
                    canOpenDetails ? 'pointer-cursor' : ''
                  }`}
                  style={{ aspectRatio: '1 / 1', cursor: canOpenDetails ? 'pointer' : 'default' }}
                  onClick={() => {
                    if (canOpenDetails && typeof onOpenDocument === 'function') {
                      onOpenDocument(docId);
                    }
                  }}
                >
                  {!doc && <span className="text-muted small">{t('profiles.documentNotFound')}</span>}
                  {doc && preview.isLoading && <span className="text-muted small">{t('profiles.loadingPreview')}</span>}
                  {doc && !preview.isLoading && preview.error && <span className="text-danger small">{preview.error}</span>}
                  {doc && !preview.isLoading && !preview.error && hasImage && (
                    <img
                      src={preview.url}
                      alt={item.label}
                      className="img-fluid"
                      style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto' }}
                    />
                  )}
                  {doc && !preview.isLoading && !preview.error && !hasImage && (
                    <span className="text-muted small">{t('profiles.previewUnavailable')}</span>
                  )}
                  {canRefreshPreview ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary app-document-preview-refresh-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        refreshDocumentPreview(docId);
                      }}
                      disabled={isRefreshingPreview}
                      title="Refresh preview"
                    >
                      <i className={`ti ${isRefreshingPreview ? 'ti-loader-2' : 'ti-refresh'}`}></i>
                    </button>
                  ) : null}
                </div>

                <div className="d-flex align-items-center justify-content-between gap-2">
                  <small className="text-muted text-truncate">{documentType}</small>
                  <span className={`badge text-uppercase ${statusMeta.className}`}>{statusMeta.label}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  return (
    <div>
      {showInlineAlerts && verifyError ? <div className="alert alert-danger py-2">{verifyError}</div> : null}
      {showInlineAlerts && verifySuccess ? <div className="alert alert-success py-2">{verifySuccess}</div> : null}

      <div className="d-md-none d-flex flex-column gap-3">
        <div className="card card-body">
          <div className="row g-2">
            <div className="col-6 d-grid">
              <button
                type="button"
                className={`btn ${mobileSection === 'profile' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setMobileSection('profile')}
              >
                {t('profiles.profileInformation')}
              </button>
            </div>
            <div className="col-6 d-grid">
              <button
                type="button"
                className={`btn ${mobileSection === 'documents' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setMobileSection('documents')}
              >
                {t('nav.documents')}
              </button>
            </div>
          </div>
        </div>

        <div className="card card-body">
          {mobileSection === 'documents'
            ? documentsContent('show-other-documents-mobile')
            : (
              renderProfileSectionTabs('profile-verification-mobile-profile')
            )}
        </div>
      </div>

      <div className="row g-4 align-items-start d-none d-md-flex">
        <div className="col-12 col-md-6">
          <div className="card card-body h-100">
            {renderProfileSectionTabs('profile-verification-profile')}
          </div>
        </div>

        <div className="col-12 col-md-6">
          <div className="card card-body h-100">
            {documentsContent('show-other-documents')}
          </div>
        </div>
      </div>

      <div className="d-flex gap-2 mt-3">
        <button
          type="button"
          className="btn btn-success"
          onClick={onVerifyProfile}
          disabled={isProfileActionBusy}
        >
          {isVerifying ? t('profiles.verifying') : t('profiles.verifyProfile')}
        </button>
        <button
          type="button"
          className="btn btn-default"
          onClick={onRequestInformation}
          disabled={isProfileActionBusy}
        >
          {isRequestingInformation ? t('profiles.requesting') : t('profiles.requestInformation')}
        </button>
        {isProfileInvalid ? (
          <button
            type="button"
            className="btn btn-outline-success"
            onClick={() => openInvalidationModal('validate')}
            disabled={isProfileActionBusy}
          >
            {isValidatingProfile ? t('profiles.validatingProfile') : t('profiles.validateProfile')}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => openInvalidationModal('invalidate')}
            disabled={isProfileActionBusy}
          >
            {isInvalidatingProfile ? t('profiles.invalidatingProfile') : t('profiles.invalidateProfile')}
          </button>
        )}
      </div>

      {invalidationAction ? (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {invalidationAction === 'invalidate'
                      ? t('profiles.invalidateProfileTitle')
                      : t('profiles.validateProfileTitle')}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label={t('common.close')}
                    onClick={closeInvalidationModal}
                    disabled={isProfileInvalidationSubmitting}
                  />
                </div>
                <div className="modal-body">
                  <p className="text-muted">
                    {invalidationAction === 'invalidate'
                      ? t('profiles.invalidateProfileWarning')
                      : t('profiles.validateProfileWarning')}
                  </p>
                  <label className="form-label" htmlFor="profile-invalidation-reason">
                    {t('profiles.invalidationReason')}
                  </label>
                  <input
                    id="profile-invalidation-reason"
                    type="text"
                    className={`form-control${invalidationReasonError ? ' is-invalid' : ''}`}
                    value={invalidationReason}
                    onChange={(event) => {
                      setInvalidationReason(event.target.value);
                      if (invalidationReasonError) {
                        setInvalidationReasonError('');
                      }
                    }}
                    disabled={isProfileInvalidationSubmitting}
                  />
                  {invalidationReasonError ? (
                    <div className="invalid-feedback d-block">{invalidationReasonError}</div>
                  ) : null}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={closeInvalidationModal}
                    disabled={isProfileInvalidationSubmitting}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    className={invalidationAction === 'invalidate' ? 'btn btn-danger' : 'btn btn-success'}
                    onClick={submitInvalidationAction}
                    disabled={isProfileInvalidationSubmitting}
                  >
                    {invalidationAction === 'invalidate'
                      ? (isInvalidatingProfile ? t('profiles.invalidatingProfile') : t('profiles.invalidateProfile'))
                      : (isValidatingProfile ? t('profiles.validatingProfile') : t('profiles.validateProfile'))}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}

      <SecurityStepUpModal
        isOpen={documentSecurityStepUp.isOpen}
        isSubmitting={documentSecurityStepUp.isSubmitting}
        error={documentSecurityStepUp.error}
        onSubmit={submitDocumentSecurityStepUp}
        onCancel={closeDocumentSecurityStepUp}
      />
    </div>
  );
}

export default ProfileVerificationTab;
